import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { Button, Form, Modal, Skeleton, Switch } from 'antd';
import { v4 } from 'uuid';
import { UploadFile } from 'antd/lib/upload/interface';
import { applyMask, removeMask } from '@/utils/mask';
import { cpfMask } from '@/utils/mask/commonMasks';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import EmployeePersonalForm from '@/components/Employee/EmployeePersonalForm';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import { SaveButton } from '@/components/_common/Buttons';
import {
  useDeleteEmployee,
  useEmployee,
  useEmployeeUpdate,
} from '@/core/domain/enterprise/Employees/Employees.hooks';
import { EmployeeEditFormValues } from '@/core/domain/enterprise/Employees/Employees.types';
import { useEmployeeFormContext } from '@/components/Employee/EmployeeFormProvider';
import handlerPersonalUpdate from '@/components/Employee/EmployeeFormProvider/handlers/personal';
import { AxiosError } from 'axios';
import { useDefaultFormErrorNotification } from '@/hooks';
import { PageProps } from '@/types/PageProps';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import EmployeeFooter from '@/components/Employee/EmployeeFooter';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const EmployeePersonalPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [employeeActive, setEmployeeActive] = useState(false);
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const { isLoading, forms, instanceForms, employeeId, disabled } =
    useEmployeeFormContext();

  const [form] = Form.useForm<EmployeeEditFormValues>(
    instanceForms.employeePersonal,
  );

  const employeeDetail = useEmployee(employeeId);
  const employeeUpdate = useEmployeeUpdate();
  const removeMutation = useDeleteEmployee();

  const { setParamLabel } = useBreadcrumbs();

  useEffect(() => {
    if (employeeDetail.data && !forms.employeePersonal) {
      const { photo, ...restData } = employeeDetail.data;

      setEmployeeActive(restData.active);

      const guid = encodeURIComponent(employeeDetail.data.redirectKey);

      let formData: EmployeeEditFormValues = {
        ...restData,
        birthday: employeeDetail.data.birthday
          ? moment(employeeDetail.data.birthday)
          : undefined,
        cpf: employeeDetail.data.cpf
          ? applyMask(employeeDetail.data.cpf, cpfMask)
          : '',
        rg: employeeDetail.data.rg
          ? removeMask(employeeDetail.data.rg)
          : undefined,
        companiesIds: employeeDetail.data.companies.map((c) => c.id),
        redirectKey: `${process.env.REACT_APP_GP_URL}/productive?guid=${guid}`,
      };

      if (photo) {
        const img: UploadFile = {
          uid: v4(),
          name: employeeDetail.data.photo,
          url: employeeDetail.data.photo,
          status: 'done',
        };

        formData = { ...formData, photo: [img] };
      }

      form.setFieldsValue({ ...formData });

      setParamLabel(':employeeId', employeeDetail.data.name);
    }
  }, [employeeDetail.data, forms.employeePersonal]);

  const handleSaveClick = async () => {
    try {
      await form.validateFields();
      form.submit();
    } catch {}
  };

  const handleChangeActive = async (active = employeeActive) => {
    try {
      const fieldValues = await form.validateFields();

      await handlerPersonalUpdate(fieldValues, (values) =>
        employeeUpdate.mutateAsync({ ...values, active }),
      );

      setEmployeeActive(active);
    } catch (err: unknown) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'employee.remove.modal.title' }),
      content: `${intl.formatMessage({
        id: 'employee.remove.modal.content',
      })} "${employeeDetail.data?.name}"`,
      onOk: async () => {
        await removeMutation.mutateAsync(Number(employeeId));
        history.push('/app/registry/employees');
      },
      okText: intl.formatMessage({
        id: 'employee.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  return (
    <DefaultRouteTabs
      route={route.parent}
      permissions={route.permissions}
      baseKey={String(employeeId)}
      basePath={`/app/registry/employees/${employeeId}`}
      extra={
        <SpaceForButton>
          <Switch
            checked={employeeActive}
            onChange={handleChangeActive}
            loading={employeeUpdate.isLoading}
            unCheckedChildren={intl.formatMessage({
              id: 'commons.entity.inactive',
            })}
            checkedChildren={intl.formatMessage({
              id: 'commons.entity.active',
            })}
            disabled={!hasPermissions([Authorities.REGISTER_EMPLOYEE_EDIT])}
          />

          <Button
            block
            type="ghost"
            danger
            onClick={handleRemoveClick}
            disabled={!hasPermissions([Authorities.REGISTER_EMPLOYEE_DELETE])}
          >
            <FormattedMessage id="commons.entity.remove" />
          </Button>

          <SaveButton
            block
            loading={isLoading}
            type="primary"
            onClick={() => handleSaveClick()}
            disabled={
              disabled || !hasPermissions([Authorities.REGISTER_EMPLOYEE_EDIT])
            }
          >
            <FormattedMessage id="layout.menu.registry.employee.saveText" />
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <Skeleton active loading={employeeDetail.isLoading || !employeeId}>
        <EmployeePersonalForm
          form={form}
          isEdit
          redirectKey={employeeDetail.data?.redirectKey}
        />

        <EmployeeFooter
          disabled={disabled}
          form={form}
          employee={employeeDetail.data}
        />
      </Skeleton>
    </DefaultRouteTabs>
  );
};

export default EmployeePersonalPage;
